import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { sendInterestConfirmationEmail } from '../../lib/email';
import {
  CheckCircleIcon,
  ClockIcon,
  LockClosedIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const INVITATION_SOURCES = [
  'Direct invitation from organiser',
  'Referred by a friend or colleague',
  'Email newsletter',
  'Social media',
  'Corporate / sponsor invitation',
  'Golf association',
  'Previous attendee',
  'Other',
];

const DETAIL_REQUIRED_SOURCES = ['Corporate / sponsor invitation', 'Other'];

const NATIONALITIES = [
  'Afghan','Albanian','Algerian','American','Andorran','Angolan','Argentine','Armenian',
  'Australian','Austrian','Azerbaijani','Bangladeshi','Belgian','Brazilian','British',
  'Bulgarian','Cameroonian','Canadian','Chilean','Chinese','Colombian','Congolese','Croatian',
  'Cuban','Czech','Danish','Dutch','Egyptian','Emirati','Ethiopian','Finnish','French',
  'German','Ghanaian','Greek','Guinean','Hungarian','Indian','Indonesian','Iranian','Iraqi',
  'Irish','Israeli','Italian','Ivorian','Jamaican','Japanese','Jordanian','Kazakhstani',
  'Kenyan','Korean','Kuwaiti','Lebanese','Libyan','Malaysian','Mexican','Moroccan','Namibian',
  'Nepalese','New Zealander','Nigerian','Norwegian','Omani','Pakistani','Palestinian',
  'Peruvian','Filipino','Polish','Portuguese','Qatari','Romanian','Russian','Rwandan',
  'Saudi','Senegalese','Serbian','Sierra Leonean','Singaporean','Somali','South African',
  'Spanish','Sri Lankan','Sudanese','Swedish','Swiss','Syrian','Tanzanian','Thai','Tunisian',
  'Turkish','Ugandan','Ukrainian','Uruguayan','Venezuelan','Vietnamese','Zambian','Zimbabwean',
];

const schema = z.object({
  first_name: z.string().min(2, 'Required'),
  last_name: z.string().min(2, 'Required'),
  email: z.string().email('Enter a valid email'),
  phone: z.string().min(7, 'Required'),
  nationality: z.string().optional(),
  invitation_source: z.string().min(1, 'Please select an option'),
  invitation_source_detail: z.string().optional(),
  arrival_date: z.string().min(1, 'Required'),
  departure_date: z.string().min(1, 'Required'),
  num_adults: z.coerce.number().int().min(1).max(20),
  num_children: z.coerce.number().int().min(0).max(20),
  room_type: z.string().min(1, 'Please select a room type'),
  special_requests: z.string().optional(),
}).refine(d => {
  if (!d.arrival_date || !d.departure_date) return true;
  return new Date(d.departure_date) >= new Date(d.arrival_date);
}, { message: 'Must be on or after arrival', path: ['departure_date'] }).refine(d => {
  if (DETAIL_REQUIRED_SOURCES.includes(d.invitation_source)) {
    return !!d.invitation_source_detail?.trim();
  }
  return true;
}, { message: 'Please provide more details', path: ['invitation_source_detail'] });

type FormData = z.infer<typeof schema>;

interface AccommodationType {
  id: string;
  name: string;
  description: string;
  cost_per_night: number;
  currency: string;
}

const FALLBACK_ROOMS: AccommodationType[] = [
  { id: 'none', name: 'No room required', description: 'I have my own accommodation', cost_per_night: 0, currency: 'NGN' },
  { id: 'standard', name: 'Standard Room', description: 'Comfortable room with golf course views', cost_per_night: 0, currency: 'NGN' },
  { id: 'deluxe', name: 'Deluxe Room', description: 'Spacious room with premium amenities and scenic views', cost_per_night: 0, currency: 'NGN' },
  { id: 'suite', name: 'Executive Suite', description: 'Luxury suite with lounge and panoramic golf views', cost_per_night: 0, currency: 'NGN' },
  { id: 'villa', name: 'Villa', description: 'Private villa with exclusive facilities', cost_per_night: 0, currency: 'NGN' },
];

function generateReference(): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `SHIP-${date}-${rand}`;
}

const inputCls = (err: boolean) =>
  `w-full px-3 py-2.5 bg-gray-50 border rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 transition-colors ${err ? 'border-red-400 focus:ring-red-200' : 'border-gray-200 focus:ring-golf-orange/30 focus:border-golf-orange'}`;

export default function MobileInvite() {
  const [formSettings, setFormSettings] = useState({ published: false, openDate: null as string | null, closeDate: null as string | null });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [accommodations, setAccommodations] = useState<AccommodationType[]>(FALLBACK_ROOMS);
  const [submitted, setSubmitted] = useState(false);
  const [submittedRef, setSubmittedRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [natQuery, setNatQuery] = useState('');
  const [natOpen, setNatOpen] = useState(false);
  const natRef = useRef<HTMLDivElement>(null);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { num_adults: 1, num_children: 0, room_type: '', invitation_source: '', invitation_source_detail: '' },
  });

  const selectedSource = watch('invitation_source');
  const selectedRoom = watch('room_type');

  useEffect(() => {
    fetchSettings();
    fetchAccommodations();
  }, []);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (natRef.current && !natRef.current.contains(e.target as Node)) setNatOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await supabase.from('system_settings').select('key, value')
        .in('key', ['interest_form_published', 'interest_form_open_date', 'interest_form_close_date']);
      if (data) {
        const map: Record<string, string> = {};
        data.forEach(r => { map[r.key] = typeof r.value === 'string' ? r.value.replace(/^"|"$/g, '') : String(r.value); });
        setFormSettings({ published: map['interest_form_published'] === 'true', openDate: map['interest_form_open_date'] || null, closeDate: map['interest_form_close_date'] || null });
      }
    } catch { } finally { setSettingsLoading(false); }
  };

  const fetchAccommodations = async () => {
    try {
      const { data } = await supabase.from('invitation_accommodation_types').select('*').eq('is_active', true).order('sort_order', { ascending: true });
      if (data && data.length > 0) setAccommodations(data);
    } catch { }
  };

  const isWithinWindow = () => {
    const now = new Date();
    if (formSettings.openDate && new Date(formSettings.openDate) > now) return false;
    if (formSettings.closeDate && new Date(formSettings.closeDate) < now) return false;
    return true;
  };

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    const reference = generateReference();
    try {
      const base = {
        first_name: data.first_name, last_name: data.last_name, email: data.email, phone: data.phone,
        nationality: data.nationality || '', arrival_date: data.arrival_date, departure_date: data.departure_date,
        num_adults: data.num_adults, num_children: data.num_children,
        room_type: data.room_type, special_requests: data.special_requests || '', reference, status: 'new',
      };
      let res = await supabase.from('interest_submissions').insert({ ...base, invitation_source: data.invitation_source, invitation_source_detail: data.invitation_source_detail || '' });
      if (res.error?.message.includes('invitation_source_detail')) res = await supabase.from('interest_submissions').insert({ ...base, invitation_source: data.invitation_source });
      if (res.error?.message.includes('invitation_source')) res = await supabase.from('interest_submissions').insert(base);
      if (res.error) throw res.error;
      await sendInterestConfirmationEmail(data.email, data.first_name, data.last_name, reference, data.arrival_date, data.departure_date, data.room_type, data.num_adults, data.num_children);
      setSubmittedRef(reference);
      setSubmitted(true);
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredNats = natQuery.length > 0
    ? NATIONALITIES.filter(n => n.toLowerCase().startsWith(natQuery.toLowerCase())).slice(0, 6)
    : [];

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  const now = new Date();
  const isClosed = formSettings.closeDate ? new Date(formSettings.closeDate) < now : false;
  const isNotYetOpen = formSettings.openDate ? new Date(formSettings.openDate) > now : false;
  const isAvailable = formSettings.published && isWithinWindow();

  if (!isAvailable) {
    return (
      <div className="px-4 pt-4 pb-10">
        <div className="bg-gray-800 rounded-2xl p-6 text-white text-center">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3">
            {isClosed ? <LockClosedIcon className="w-6 h-6 text-gray-300" /> : <ClockIcon className="w-6 h-6 text-yellow-300" />}
          </div>
          <h2 className="font-display font-bold text-lg mb-2">
            {isClosed ? 'Invitations Closed' : isNotYetOpen ? 'Coming Soon' : 'Not Available'}
          </h2>
          <p className="text-gray-400 text-sm leading-relaxed">
            {isClosed
              ? 'RSVPs for the Ibom Open have closed. Thank you for your interest.'
              : isNotYetOpen
                ? `Invitations open on ${formSettings.openDate ? new Date(formSettings.openDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'a future date'}.`
                : 'This invitation is not currently available.'}
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="px-4 pt-4 pb-10">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="font-display text-xl font-bold text-golf-gray mb-2">RSVP Confirmed</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-5">
            Thank you for accepting your invitation. A confirmation has been sent to your email. Our team will be in touch shortly.
          </p>
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <p className="text-xs text-gray-400 mb-1">Your reference number</p>
            <p className="font-mono font-bold text-golf-orange text-base tracking-widest">{submittedRef}</p>
          </div>
          <p className="text-xs text-gray-400 mt-3">Please retain this reference for your records.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-6">
      {/* Compact hero banner */}
      <div className="relative bg-golf-gray overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=800')" }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(26,26,26,0.2) 0%, rgba(26,26,26,0.8) 100%)' }} />
        <div className="relative px-4 pt-5 pb-6 text-white">
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-semibold mb-2">You are invited to attend</p>
          <h1 className="font-display font-bold text-3xl leading-tight mb-3">
            Ibom Open<br />2026
          </h1>
          <p className="text-white/60 text-sm leading-relaxed mb-4">
            We are delighted to extend this invitation to you. Please complete the form below to confirm your attendance, travel dates, and accommodation preferences.
          </p>
          {formSettings.closeDate && (
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-xl px-3 py-2">
              <ClockIcon className="w-3.5 h-3.5 text-golf-orange flex-shrink-0" />
              <span className="text-xs text-white/50">
                RSVP by <span className="text-white/80 font-semibold">
                  {new Date(formSettings.closeDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="px-4 pt-4 space-y-3">

        {/* Guest Details */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <h3 className="font-display font-bold text-golf-gray text-sm mb-3">Guest Details</h3>
          <div className="space-y-3">
            <MobileField label="First Name" error={errors.first_name?.message}>
              <input {...register('first_name')} placeholder="John" className={inputCls(!!errors.first_name)} />
            </MobileField>
            <MobileField label="Last Name" error={errors.last_name?.message}>
              <input {...register('last_name')} placeholder="Doe" className={inputCls(!!errors.last_name)} />
            </MobileField>
            <MobileField label="Email Address" error={errors.email?.message}>
              <input {...register('email')} type="email" placeholder="john@example.com" className={inputCls(!!errors.email)} />
            </MobileField>
            <MobileField label="Phone Number" error={errors.phone?.message}>
              <input {...register('phone')} type="tel" placeholder="+234 800 000 0000" className={inputCls(!!errors.phone)} />
            </MobileField>
            <MobileField label="Nationality" error={errors.nationality?.message}>
              <div ref={natRef} className="relative">
                <input
                  value={natQuery}
                  onChange={e => { setNatQuery(e.target.value); setValue('nationality', e.target.value); setNatOpen(true); }}
                  onFocus={() => natQuery && setNatOpen(true)}
                  placeholder="e.g. Nigerian, South African..."
                  autoComplete="off"
                  className={inputCls(false)}
                />
                {natOpen && filteredNats.length > 0 && (
                  <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-44 overflow-y-auto">
                    {filteredNats.map(nat => (
                      <li
                        key={nat}
                        onMouseDown={e => { e.preventDefault(); setNatQuery(nat); setValue('nationality', nat); setNatOpen(false); }}
                        className="px-3 py-2.5 text-sm cursor-pointer hover:bg-golf-orange/10 hover:text-golf-orange transition-colors"
                      >
                        {nat}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </MobileField>
          </div>
        </div>

        {/* Source of Invitation */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <h3 className="font-display font-bold text-golf-gray text-sm mb-1">Source of Invitation</h3>
          <p className="text-xs text-gray-400 mb-3">How did you hear about this invitation?</p>
          <div className="space-y-1.5">
            {INVITATION_SOURCES.map(src => {
              const active = selectedSource === src;
              return (
                <button
                  key={src}
                  type="button"
                  onClick={() => setValue('invitation_source', src, { shouldValidate: true })}
                  className={`flex items-center justify-between w-full px-3 py-2.5 rounded-xl border text-sm text-left transition-colors ${active ? 'bg-golf-orange/5 border-golf-orange text-golf-orange font-medium' : 'bg-gray-50 border-gray-200 text-gray-700'}`}
                >
                  <span>{src}</span>
                  {active && <CheckIcon className="w-4 h-4 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          {errors.invitation_source && <p className="text-red-500 text-xs mt-2">{errors.invitation_source.message}</p>}
          {DETAIL_REQUIRED_SOURCES.includes(selectedSource) && (
            <div className="mt-3">
              <MobileField
                label={selectedSource === 'Corporate / sponsor invitation' ? 'Corporate / Sponsor name' : 'Please specify'}
                error={errors.invitation_source_detail?.message}
              >
                <input
                  {...register('invitation_source_detail')}
                  placeholder={selectedSource === 'Corporate / sponsor invitation' ? 'e.g. Ship Afirka LLC' : 'Please describe...'}
                  className={inputCls(!!errors.invitation_source_detail)}
                />
              </MobileField>
            </div>
          )}
        </div>

        {/* Attendance & Travel */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <h3 className="font-display font-bold text-golf-gray text-sm mb-3">Attendance & Travel</h3>
          <div className="space-y-3">
            <MobileField label="Intended Arrival Date" error={errors.arrival_date?.message}>
              <input {...register('arrival_date')} type="date" className={`${inputCls(!!errors.arrival_date)} [color-scheme:light]`} />
            </MobileField>
            <MobileField label="Intended Departure Date" error={errors.departure_date?.message}>
              <input {...register('departure_date')} type="date" className={`${inputCls(!!errors.departure_date)} [color-scheme:light]`} />
            </MobileField>
            <div className="grid grid-cols-2 gap-3">
              <MobileField label="Number of Adults" error={errors.num_adults?.message}>
                <input {...register('num_adults')} type="number" min={1} max={20} className={inputCls(!!errors.num_adults)} />
              </MobileField>
              <MobileField label="Number of Children" error={errors.num_children?.message}>
                <input {...register('num_children')} type="number" min={0} max={20} className={inputCls(!!errors.num_children)} />
              </MobileField>
            </div>
          </div>
        </div>

        {/* Accommodation Preference */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <h3 className="font-display font-bold text-golf-gray text-sm mb-1">Accommodation Preference</h3>
          <p className="text-xs text-gray-400 mb-3">Select your preferred room type. Final availability confirmed upon acceptance of your invitation.</p>
          <div className="space-y-1.5">
            {accommodations.map(opt => {
              const active = selectedRoom === opt.id;
              const costStr = opt.cost_per_night > 0 ? `${opt.currency} ${opt.cost_per_night.toLocaleString()}/night` : null;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setValue('room_type', opt.id, { shouldValidate: true })}
                  className={`flex items-start gap-3 w-full px-3 py-2.5 rounded-xl border text-left transition-colors ${active ? 'bg-golf-orange/5 border-golf-orange' : 'bg-gray-50 border-gray-200'}`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${active ? 'border-golf-orange bg-golf-orange' : 'border-gray-300 bg-white'}`}>
                    {active && <span className="w-1.5 h-1.5 bg-white rounded-full" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className={`text-sm font-medium ${active ? 'text-golf-orange' : 'text-gray-800'}`}>{opt.name}</span>
                      {costStr && <span className="text-xs text-gray-500 flex-shrink-0">{costStr}</span>}
                    </div>
                    <p className="text-xs text-gray-500 leading-snug">{opt.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
          {errors.room_type && <p className="text-red-500 text-xs mt-2">{errors.room_type.message}</p>}
        </div>

        {/* Additional Notes */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <h3 className="font-display font-bold text-golf-gray text-sm mb-3">Additional Notes</h3>
          <MobileField label="Any dietary requirements, accessibility needs, or special requests?" error={errors.special_requests?.message}>
            <textarea
              {...register('special_requests')}
              rows={3}
              placeholder="Please let us know of any requirements we should accommodate..."
              className={`${inputCls(false)} resize-none`}
            />
          </MobileField>
        </div>

        {/* Privacy note */}
        <div className="flex items-start gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <LockClosedIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-gray-500 leading-relaxed">
            Your details are handled in strict confidence and will only be used in connection with your invitation to the Ibom Open Golf Tournament. Our team will be in touch to confirm your place and arrange the finer details.
          </p>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-4 bg-golf-orange text-white font-bold rounded-2xl active:opacity-80 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed text-base"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Sending RSVP...
            </span>
          ) : 'Accept Invitation'}
        </button>

        <div className="pb-2" />
      </form>
    </div>
  );
}

function MobileField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1.5">{label}</label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}
